import { Injectable } from '@angular/core';
import * as _ from 'lodash';

export interface Operator {
  op: string;
  text: string;
}

export interface Action {
  value: string;
  text: string;
}

export interface SkipLogic {
  src_field: any;
  is_primary_rating: boolean | null;
  state: string;
  value: any;
  dest_field: any;
  target_type: 'target_value' | 'target_field';
  op: string;
  err?: boolean;
  errText?: string;
  expressionText?: string;
  expressionString?: string;
  expression?: string;
  e?: string;
}

@Injectable({
  providedIn: 'root'
})
export class QueryBuilderService {
  operators: { [key: string]: Operator } = {
    "$eq": {
      op: "==",
      text: "Equals"
    },
    "$ne": {
      op: "!=",
      text: "Not Equals"
    },
    "$gt": {
      op: ">",
      text: "Greater than"
    },
    "$gte": {
      op: ">=",
      text: "Greater than Equals"
    },
    "$lt": {
      op: "<",
      text: "Less than"
    },
    "$lte": {
      op: "<=",
      text: "Less than Equals"
    },
    "$or": {
      op: "||",
      text: "OR"
    },
    "$and": {
      op: "&&",
      text: "AND"
    }
  };

  actions: { [key: string]: Action } = {
    '1': {
      value: 'visible-if',
      text: "Show"
    },
    '2': {
      value: 'is_mandatory_if',
      text: "Mandatory"
    },
    '3': {
      value: 'server-visible-if',
      text: "Server Visible"
    }
  };

  skipLogics: SkipLogic[] = [];
  selectedLogic: { logic_action: string; customExpression: string | null } = { logic_action: '1', customExpression: null };

  constructor() {
    this.initConditions();
  }

  initConditions(): void {
    this.skipLogics = [];
    this.selectedLogic = { logic_action: '1', customExpression: null };
    this.newSkipLogic();
  }

  newSkipLogic(value?: string): void {
    this.skipLogics.push({
      src_field: null,
      is_primary_rating: null,
      state: '$eq',
      value: null,
      dest_field: null,
      target_type: 'target_value',
      op: value || '$or'
    });
  }

  validateFields(): boolean {
    let isErr: boolean | null = null;
    _.each(this.skipLogics, (logic) => {
      if (logic.src_field && logic.target_type && logic.state) {
        if (logic.target_type === 'target_value' && logic.value === null) {
          logic.err = true;
          logic.errText = 'Value is required.';
          isErr = true;
        } else if (logic.target_type === 'target_field') {
          if (logic.dest_field === null) {
            logic.err = true;
            logic.errText = 'Interaction is required.';
            isErr = true;
          } else if (logic.src_field.field_id === logic.dest_field.field_id) {
            logic.err = true;
            logic.errText = 'Same interaction is not allowed.';
            isErr = true;
          } else {
            logic.err = false;
            isErr = false;
          }
        } else {
          logic.err = false;
          isErr = false;
        }
      } else {
        logic.err = true;
      }
    });
    return !isErr;
  }

  removeSkipLogic(index: number): void {
    this.skipLogics.splice(index, 1);
  }

  updateLogic(index: number, logicKey: string, targetType: string, value: any): void {
    const logic = this.skipLogics[index];
    if (targetType === 'target_field') {
      this.skipLogics[index]['value'] = null;
    }

    let preS1 = '';
    if (logic && logic.src_field) {
      preS1 = (logic.src_field.is_primary_rating || logic.src_field.field_id === 'primary_rating') 
        ? 'rating.primary_rating' 
        : `rating.detailed_rating['${logic.src_field.field_id}']`;
    }

    if (logic.dest_field && value === null) {
      const preS2 = (logic.dest_field.is_primary_rating || logic.dest_field.field_id === 'primary_rating')
        ? 'rating.primary_rating'
        : `rating.detailed_rating['${logic.dest_field.field_id}']`;

      logic.expressionText = `(${logic.src_field.text} <i style="color:green">${this.operators[logic.state].text}</i> ${logic.dest_field.text})`;
      logic.expressionString = `(${logic.src_field.text} <i style="color:green">${this.operators[logic.state].text}</i> ${logic.dest_field.text})`;
      logic.expression = `${logic.src_field.field_id}${this.operators[logic.state].op}${logic.dest_field.field_id}`;
      logic.e = `${preS1}${this.operators[logic.state].op}${preS2}`;
      logic.value = null;
    } else if (value !== null) {
      const formattedValue = typeof value === 'string' ? `'${value}'` : value;
      logic.expressionText = `(${logic.src_field.text} <i style="color:green">${this.operators[logic.state].text}</i> <span style="color:#80077f">${formattedValue}</span>)`;
      logic.expressionString = `(${logic.src_field.text} <i style="color:green">${this.operators[logic.state].text}</i> <span style="color:#80077f">${formattedValue}</span>)`;
      logic.expression = `${logic.src_field.field_id}${this.operators[logic.state].op}${formattedValue}`;
      logic.e = `${preS1}${this.operators[logic.state].op}${formattedValue}`;
      logic.dest_field = null;
    }
  }

  private createExpression(logics: SkipLogic[], isUpdate: boolean = false): { expression: string; expressionString: string } {
    let expression = '';
    let expressionString = '';

    if (logics && logics.length > 0) {
      _.each(logics, (logic, index) => {
        if (logic && logic.src_field) {
          if (!isUpdate) logic.src_field = logic.src_field.field_id;
          if (logic.e && logic.op) {
            expression += logic.e;
            expressionString += logic.expressionString;
            if (index < logics.length - 1) {
              expression += this.operators[logic.op].op;
              expressionString += this.operators[logic.op].text;
            }
          }
        }
      });
    }

    return { expression, expressionString };
  }

  private isCustomExpressionAvailable(): boolean {
    return !!this.selectedLogic.customExpression && this.selectedLogic.customExpression.trim().length > 1;
  }

  saveSkipLogic(interaction: any): void {
    const selectedAction = this.actions[this.selectedLogic.logic_action];

    if (selectedAction && this.isCustomExpressionAvailable()) {
      if (!interaction.logicDetail) {
        interaction.logicDetail = {};
      }
      interaction.logicDetail[this.selectedLogic.logic_action] = {
        logicString: this.selectedLogic.customExpression,
        logicKey: this.selectedLogic.logic_action
      };
      interaction[selectedAction.value] = this.selectedLogic.customExpression;
      this.initConditions();
    } else if (this.validateFields()) {
      if (this.selectedLogic && this.selectedLogic.logic_action) {
        const logics = _.clone(this.skipLogics);
        if (logics && logics.length > 0 && logics[0].e) {
          const { expression, expressionString } = this.createExpression(logics);
          const action = this.actions[this.selectedLogic.logic_action];

          if (expression && action && interaction && interaction.field_id) {
            interaction[action.value] = expression;
            if (!interaction.logicDetail) {
              interaction.logicDetail = {};
            }
            interaction.logicDetail[this.selectedLogic.logic_action] = {
              logicString: expressionString,
              logicKey: this.selectedLogic.logic_action
            };
            this.initConditions();
          }
        } else {
          this.initConditions();
        }
      }
    }
  }

  excludeLogic(interaction: any, fieldId: string, actionKey: string): void {
    const action = this.actions[actionKey].value;
    delete interaction[action];
    delete interaction.logicDetail[actionKey];

    if (_.isEmpty(interaction.logicDetail)) {
      delete interaction['logicDetail'];
    }
  }
} 